import * as React from 'react';
// Import material ui modules
import {
	ArrowForward,
	ArrowBack,
	Check,
} from '@mui/icons-material';
// Import hooks.
import {
	useSubformBuildingInfo,
	UseSubformBuildingInfoResults,
	useSubformBuildingLimits,
	UseSubformBuildingLimitsResults,
	useSubformPropertyAddress,
	UseSubformPropertyAddressResults,
} from '@app/hooks/view';
import {
	UseInputAddressState,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupConfirmationProps,
	FormProps,
	NavigationFooterProps,
	FormBuildingFullAddProps,
	ScrollbarProps,
	StatusProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	BuildingFoundationType,
	NavigationFooterAlignment,
} from '@app/enumerations';
// Import api models.
import {
	Address,
	AddressLocationAsMailingAddress,
	BuildingRequestCommercial,
	GpsCoordinates,
	ThirdPartyPropertyDataCommercial,
} from '@app/models/api';

interface UseFormBuildingFullAddParameters {
	id: string;
	props: FormBuildingFullAddProps,
}

interface UseFormBuildingFullAddResults {
	areAllInputsDisabled: boolean;
	showFormPostPreliminaryAddressVerification: boolean;
	subformPropertyAddress: UseSubformPropertyAddressResults;
	subformBuildingInfo: UseSubformBuildingInfoResults;
	subformBuildingLimits: UseSubformBuildingLimitsResults;
	submitForm: () => void;
	applyFormProps: () => FormProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	applySubmissionFormPopupConfirmationProps: () => FormPopupConfirmationProps;
}

const useFormBuildingFullAdd = (params: UseFormBuildingFullAddParameters): UseFormBuildingFullAddResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		if (preliminaryVerifiedAddress.hasPreliminaryAddressBeenVerified) {
			// First flag all inputs to show any validation errors
			flagAllInputsToShowValidations();
			// Validate the whole form.
			let isValid: boolean = checkFormValidation(true);
			// Make sure the confirmation popup appears if it's required.
			if (isValid) {
				if (requireConfirmationOnSave) {
					setOpenSaveConfirmationPopup(true);
				}
				else if (preliminaryVerifiedAddress.address !== undefined) {
					params.props.onSaveBuilding(createBuildingRequestCommercial(preliminaryVerifiedAddress.address));
				}
				else {
					subformPropertyAddress.verifyAddress();
				}
			}
		}
		else {
			// Flag all property address subform inputs to show validations.
			subformPropertyAddress.flagAllInputsToShowValidations();
			// Validate the property address subform.
			let isValid: boolean = checkSubformPropertyAddressValidation(true);
			if (isValid) {
				if (requireConfirmationOnSave) {
					setOpenSaveConfirmationPopup(true);
				}
				else {
					subformPropertyAddress.verifyAddress();
				}
			}
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Building',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
			statusProps: applyStatusProps(),
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		const isSaveRequired: boolean = doesFormRequireSave();
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: subformPropertyAddress.addressGeocoding.isAddressCoordinateSelectionRequired()
				|| subformPropertyAddress.addressGeocoding.addressVerification.isAddressVerificationRequired()
				|| subformPropertyAddress.addressGeocoding.addressVerification.isSingleLineAddressVerificationRequired(),
			rightButtonLabel: isSaveRequired ? 'Save' : 'Continue',
			rightButtonClick: isSaveRequired ? submitForm : params.props.onForward,
			rightButtonIcon: isSaveRequired ? Check : ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	/**
	 * Returns props for the submission confirmation popup form. 
	 **/
	const applySubmissionFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openSaveConfirmationPopup,
			onClose: cancelConfirmSubmission,
			onConfirm: confirmSubmission,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	/**
	 * Called when the address is finished verifying.
	 */ 
	const onFinishVerifyAddress = async (address: AddressLocationAsMailingAddress, coordinates: GpsCoordinates): Promise<void> => {
		if (preliminaryVerifiedAddress.hasPreliminaryAddressBeenVerified) {
			params.props.onSaveBuilding(createBuildingRequestCommercial(address));
		}
		else {
			const addressRecord: Address | undefined = address.address ?? undefined;
			const lat: number | undefined = coordinates.latitude ?? undefined;
			const lng: number | undefined = coordinates.longitude ?? undefined;

			if (addressRecord !== undefined
				&& lat !== undefined
				&& lng !== undefined) {
				const result: ThirdPartyPropertyDataCommercial = await params.props.addressProps.getThirdPartyPropertyDataCommercial(addressRecord, lat, lng);
				subformBuildingInfo.parkingTypeInput.setInput(result.parkingType ?? undefined);
				subformBuildingInfo.propertyInfo.constructionTypeInput.setInput(result.constructionType ?? undefined);
				subformBuildingInfo.propertyInfo.foundationType.setFoundationTypeDetail(result.foundationTypeDetail ?? undefined);
				subformBuildingInfo.propertyInfo.buildingReplacementCostValueInput.setInput(result.buildingReplacementCostValue ?? undefined);
				subformBuildingInfo.propertyInfo.designatedFloodZoneInput.setInput(result.floodZone ?? String.empty());
				subformBuildingInfo.propertyInfo.buildingUseInput.setInput(result.buildingUse ?? undefined);
				subformBuildingInfo.propertyInfo.squareFootageInput.setInput(result.squareFeet ?? undefined);
				subformBuildingInfo.propertyInfo.numberOfStoriesSelectionListInput.setInput(result.numberStoriesAboveGrade ?? undefined);
				subformBuildingInfo.propertyInfo.yearOfConstructionInput.setInput(result.yearOfConstruction ?? undefined);
			}

			setPreliminaryVerifiedAddress(() => {
				return {
					address,
					hasPreliminaryAddressBeenVerified: true,
				}
			});
		}
	}

	const createBuildingRequestCommercial = (address: AddressLocationAsMailingAddress): BuildingRequestCommercial => {
		return {
			address: address,
			buildingData: subformBuildingInfo.createBuildingRiskDataCommercial(),
			limits: subformBuildingLimits.createBuildingLimitsCommercial(),
		};
	}

	/**
	 * Confirms submission if user chooses to. 
	 **/
	const confirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
		if (preliminaryVerifiedAddress.hasPreliminaryAddressBeenVerified) {
			if (preliminaryVerifiedAddress.address !== undefined) {
				params.props.onSaveBuilding(createBuildingRequestCommercial(preliminaryVerifiedAddress.address));
			}
			else {
				subformPropertyAddress.verifyAddress();
			}
		}
		else {
			subformPropertyAddress.verifyAddress();
		}
	}

	/**
	 * Cancels the confirmation prompt for submission. 
	 **/
	const cancelConfirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
	}

	/**
	 * Validates an individual input.
	 * @param input
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation. 
	 **/
	const checkSubformPropertyAddressValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<UseInputAddressState>(subformPropertyAddress.addressGeocoding.addressVerification.addressLocationInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(subformPropertyAddress.copyToMailingAddressInput, focusToElement);
	}

	/**
	 * Method to check form validation. 
	 **/
	const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkSubformPropertyAddressValidation(focusToElement)
			&& checkInputValidation<BuildingFoundationType | undefined>(subformBuildingInfo.propertyInfo.foundationType.foundationTypeSelectionListInput, focusToElement)
			&& checkInputValidation<string | undefined>(subformBuildingInfo.propertyInfo.foundationType.foundationTypeDetailSelectionListInput, focusToElement)
			&& checkInputValidation<string>(subformBuildingInfo.buildingDescriptionInput, focusToElement)
			&& checkInputValidation<number | undefined>(subformBuildingInfo.propertyInfo.squareFootageInput, focusToElement)
			&& checkInputValidation<number | undefined>(subformBuildingInfo.propertyInfo.numberOfStoriesSelectionListInput, focusToElement)
			&& checkInputValidation<string | undefined>(subformBuildingInfo.propertyInfo.constructionTypeInput, focusToElement)
			&& checkInputValidation<string | undefined>(subformBuildingInfo.propertyInfo.buildingUseInput, focusToElement)
			&& checkInputValidation<string | undefined>(subformBuildingInfo.parkingTypeInput, focusToElement)
			&& checkInputValidation<number | undefined>(subformBuildingInfo.propertyInfo.yearOfConstructionInput, focusToElement)
			&& checkInputValidation<number | undefined>(subformBuildingInfo.propertyInfo.buildingReplacementCostValueInput, focusToElement)
			&& checkInputValidation<string>(subformBuildingInfo.propertyInfo.designatedFloodZoneInput, focusToElement)
			&& checkInputValidation<number | undefined>(subformBuildingLimits.buildingLimitInput, focusToElement)
			&& checkInputValidation<number | undefined>(subformBuildingLimits.contentsLimitInput, focusToElement)
			&& checkInputValidation<number | undefined>(subformBuildingLimits.businessIncomeExtraExpenseLimitInput, focusToElement);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		subformPropertyAddress.flagAllInputsToShowValidations();
		subformBuildingInfo.flagAllInputsToShowValidations();
		subformBuildingLimits.flagAllInputsToShowValidations();
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doesFormHaveChanges = (): boolean => {
		return subformPropertyAddress.doInputsHaveChanges()
			|| subformBuildingInfo.doInputsHaveChanges()
			|| subformBuildingLimits.doInputsHaveChanges();
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generates id for property address subform. 
	 **/
	const generateIdForPropertyAddressSubform = (): string => {
		return `${generateIdForForm()}-subform-address`;
	}

	/**
	 * Generate id for the building info subform.
	 **/
	const generateIdForBuildingInfoSubform = (): string => {
		return `${generateIdForForm()}-subform-buildingInfo`;
	}

	/**
	 * Generate id for the building limits subform.
	 **/
	const generateIdForBuildingLimitsSubform = (): string => {
		return `${generateIdForForm()}-subform-buildingLimits`;
	}

	//#endregion id generators

	/**
	 * Checks to see if the form requires saving. 
	 **/
	const doesFormRequireSave = (): boolean => {
		return params.props.allowEdits
			&& !areAllInputsDisabled
			&& (doesFormHaveChanges()
				|| params.props.isReviewRequired);
	}

	// #endregion private methods


	// #region variables

	const requireConfirmationOnSave: boolean = React.useMemo(() => params.props.requireConfirmationOnSave ?? false, [params.props.requireConfirmationOnSave]);
	const showValidation: boolean = React.useMemo(() => params.props.showValidationsOnMount ?? false, [params.props.showValidationsOnMount]);

	// State variables.
	const [openSaveConfirmationPopup, setOpenSaveConfirmationPopup] = React.useState<boolean>(false);
	const [preliminaryVerifiedAddress, setPreliminaryVerifiedAddress] = React.useState<{
		address: AddressLocationAsMailingAddress | undefined;
		hasPreliminaryAddressBeenVerified: boolean;
	}>({
		address: undefined,
		hasPreliminaryAddressBeenVerified: false,
	});

	const subformPropertyAddress = useSubformPropertyAddress({
		id: generateIdForPropertyAddressSubform(),
		allowEdits: params.props.allowEdits,
		inputSettingsProps: params.props.inputSettingsProps?.subformInputSettingsPropertyAddressProps,
		addressProps: params.props.addressProps,
		possibleStates: params.props.possibleStates,
		onFinishVerifyAddress,
		broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
		showValidationsOnMount: params.props.showValidationsOnMount,

	});
	const subformBuildingInfo = useSubformBuildingInfo({
		id: generateIdForBuildingInfoSubform(),
		allowEdits: params.props.allowEdits && preliminaryVerifiedAddress.hasPreliminaryAddressBeenVerified,
		inputSettingsProps: params.props.inputSettingsProps?.subformInputSettingsBuildingInfoProps,
		propertyDomainValuesProps: params.props.propertyDomainValuesProps,
		hasFloodPeril: params.props.hasFloodPeril,
		broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
		showValidationsOnMount: params.props.showValidationsOnMount,
	});
	const subformBuildingLimits = useSubformBuildingLimits({
		id: generateIdForBuildingLimitsSubform(),
		allowEdits: params.props.allowEdits && preliminaryVerifiedAddress.hasPreliminaryAddressBeenVerified,
		inputSettingsProps: params.props.inputSettingsProps?.subformInputSettingsBuildingLimitsProps,
		buildingReplacementCostValue: subformBuildingInfo.propertyInfo.buildingReplacementCostValueInput.value,
		showValidationsOnMount: params.props.showValidationsOnMount,
	});

	// Cached variables.

	const showStatusMessage: boolean = React.useMemo(() => params.props.isReviewRequired
		&& !String.isNullOrWhiteSpace(params.props.statusMessage)
		&& params.props.allowEdits
		&& checkFormValidation(false),
		[
			params.props.isReviewRequired,
			params.props.statusMessage,
			params.props.allowEdits,
		],
	);

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return subformPropertyAddress.areAllInputsDisabled
			&& subformBuildingInfo.areAllInputsDisabled
			&& subformBuildingLimits.areAllInputsDisabled;
	}, [
		subformPropertyAddress.areAllInputsDisabled,
		subformBuildingInfo.areAllInputsDisabled,
		subformBuildingLimits.areAllInputsDisabled,
	]);

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!showValidation
			|| checkFormValidation(true)) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			subformBuildingInfo.propertyInfo.foundationType.foundationTypeSelectionListInput.focusToInput();
		}
	}, []);

	// Make sure the existing preliminary address is removed if the address is updated.
	React.useEffect(() => {
		if (preliminaryVerifiedAddress.address !== undefined) {
			setPreliminaryVerifiedAddress((oldPreliminaryAddress) => {
				return {
					...oldPreliminaryAddress,
					address: undefined
				};
			});
		}
	}, [subformPropertyAddress.addressGeocoding.addressVerification.addressLocationInput.value]);

	// #endregion life-cycle methods.


	return {
		areAllInputsDisabled,
		showFormPostPreliminaryAddressVerification: preliminaryVerifiedAddress.hasPreliminaryAddressBeenVerified,
		subformPropertyAddress,
		subformBuildingInfo,
		subformBuildingLimits,
		submitForm,
		applyFormProps,
		applyNavigationFooterProps,
		applySubmissionFormPopupConfirmationProps,
	};
}

// Export parameters.
export {
	UseFormBuildingFullAddParameters,
}

// Export results.
export {
	UseFormBuildingFullAddResults,
}

export default useFormBuildingFullAdd;